import * as React from "react";
import Image, {ImageObject} from "/src/components/image/Image";
import {css} from "/src/utils/utils";
import * as styles from "./highlighted-button.module.scss";

export enum HighlightedButtonShape {
  PILL = "pill",
  ICON = "icons",
  NONE = "none",
}

interface HighlightedButtonProps
  extends Pick<
    Queries.CarouselFragmentFragment,
    "title" | "slides" | "alignTitle" | "highlightedButtonShape"
  > {
  onClick: (index: number, disableAutoplay?: boolean) => void;
  selectedSlideIndex: number;
  darkMode?: boolean;
}

export function HighlightedButton(props: HighlightedButtonProps) {
  const {
    alignTitle,
    highlightedButtonShape = HighlightedButtonShape.PILL,
    onClick,
    title,
    slides,
    selectedSlideIndex,
    darkMode,
  } = props;

  const handleButtonClick = React.useCallback(
    (index: number) => () => {
      onClick(index, true);
    },
    [onClick]
  );

  if (highlightedButtonShape === HighlightedButtonShape.NONE) {
    return null;
  }

  return (
    <div
      className={css(
        styles.highlightedButton,
        darkMode ? styles.highlightedButtonDark : "",
        alignTitle === "left" ? styles.highlightedButtonLeft : ""
      )}
    >
      <h2 className={css("h2", styles.highlightedButton__header)}>{title}</h2>

      <div
        className={css(
          styles.highlightedButton__buttonContainer,
          slides && slides.length < 3
            ? styles.highlightedButton__buttonContainerTwoRows
            : ""
        )}
      >
        {slides?.map((slide, index) => {
          const {timelineButtonText, highlightedIcon, nonHighlightedIcon} =
            slide || {};

          switch (highlightedButtonShape) {
            case HighlightedButtonShape.PILL:
              if (!timelineButtonText) {
                return null;
              }

              return (
                <PillButton
                  key={index}
                  selected={selectedSlideIndex === index}
                  onClick={handleButtonClick(index)}
                  timelineButtonText={timelineButtonText || undefined}
                />
              );
            case HighlightedButtonShape.ICON: {
              const image = {
                ...(selectedSlideIndex === index
                  ? highlightedIcon
                  : nonHighlightedIcon),
              };

              if (!image) {
                return null;
              }

              return (
                <div
                  key={index}
                  className={styles.highlightedButton__iconContainer}
                >
                  <IconButton
                    image={image as ImageObject}
                    onClick={handleButtonClick(index)}
                    selected={selectedSlideIndex === index}
                    timelineButtonText={timelineButtonText || undefined}
                  />
                  {index < slides.length - 1 && (
                    <div
                      className={styles.highlightedButton__iconContainer__line}
                    />
                  )}
                </div>
              );
            }
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}

interface PillButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  timelineButtonText?: string;
  selected: boolean;
}

function PillButton({onClick, selected, timelineButtonText}: PillButtonProps) {
  return (
    <button
      className={css(
        styles.highlightedButton__pillButton,
        selected ? styles.highlightedButton__pillButtonSelected : ""
      )}
      type="button"
      onClick={onClick}
    >
      <span
        data-label={timelineButtonText}
        className={styles.highlightedButton__pillButton__label}
      >
        {timelineButtonText}
      </span>
    </button>
  );
}

interface IconButtonProps {
  image: ImageObject;
  onClick: () => void;
  selected: boolean;
  timelineButtonText?: string;
}

function IconButton({
  onClick,
  image,
  selected,
  timelineButtonText,
}: IconButtonProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "enter") {
          onClick();
        }
      }}
      className={styles.highlightedButton__iconContainer__button}
    >
      <Image
        image={image}
        classes={styles.highlightedButton__iconContainer__button__icon}
      />
      <div
        className={css(
          styles.highlightedButton__iconContainer__button__text,
          selected
            ? styles.highlightedButton__iconContainer__button__textSelected
            : ""
        )}
      >
        {timelineButtonText}
      </div>
    </div>
  );
}
